import { useEffect, useState, useRef } from 'react';
import {
  FaCalendarAlt,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaCode,
  FaCog,
  FaCopy,
  FaCube,
  FaDownload,
  FaEdit,
  FaExclamationTriangle,
  FaFileAlt,
  FaFileCode,
  FaFlask,
  FaFont,
  FaGlobe,
  FaHistory,
  FaInfoCircle,
  FaKey,
  FaLayerGroup,
  FaLightbulb,
  FaLink,
  FaLock,
  FaNetworkWired,
  FaPen,
  FaPlus,
  FaProjectDiagram,
  FaQuestionCircle,
  FaSave,
  FaShieldAlt,
  FaSitemap,
  FaSpinner,
  FaTag,
  FaTimes,
  FaTrash,
} from 'react-icons/fa';
import Select from 'react-select';
import { toast } from 'react-hot-toast';

import ModalRegex from '../../../regex/components/ModalRegex/ModalRegex';
import { useRegex } from '../../hooks/useRegex';
import { justLettersWithoutAccent } from '../../../../services/helpers';
import { interoperabilityApi } from '../../../interoperability/api/interoperability.api';

import {
  COMPOSITOR_OPTIONS,
  CONSTRAINT_KIND_OPTIONS,
  CONTENT_MODEL_OPTIONS,
  FACET_LABELS,
  FORM_DEFAULT_OPTIONS,
  INHERITANCE_OPTIONS,
  NODE_KIND_OPTIONS,
  PROCESS_CONTENTS_OPTIONS,
  USE_OPTIONS,
  XSD_HELP_SECTIONS,
  XSD_TEST_EXAMPLES,
  createEmptyConstraint,
  createEmptyField,
  createEmptyNode,
  generateXsdXmlPreview,
  getApplicableFacets,
  serializeVersionForPayload,
  typeDataOptions,
  validateCardinality,
  validateFieldFacets,
} from '../../interfaces/xsd.interface';

// ESTILOS COMUNES
const INPUT_CLASS =
  'box-border w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 hover:border-slate-400 focus:border-primary focus:ring-4 focus:ring-primary/10';

const LABEL_CLASS = 'mb-2 block text-sm font-semibold text-slate-700';

const SELECT_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({ ...base, minHeight: '46px', borderRadius: '0.75rem' }),
};

// XSD help panel.
const COLOR_MAP = {
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',   badge: 'bg-blue-100 text-blue-700',   title: 'text-blue-800' },
  violet: { bg: 'bg-violet-50', border: 'border-violet-200', badge: 'bg-violet-100 text-violet-700', title: 'text-violet-800' },
  amber:  { bg: 'bg-amber-50',  border: 'border-amber-200',  badge: 'bg-amber-100 text-amber-700',  title: 'text-amber-800' },
  green:  { bg: 'bg-green-50',  border: 'border-green-200',  badge: 'bg-green-100 text-green-700',  title: 'text-green-800' },
  teal:   { bg: 'bg-teal-50',   border: 'border-teal-200',   badge: 'bg-teal-100 text-teal-700',   title: 'text-teal-800' },
  rose:   { bg: 'bg-rose-50',   border: 'border-rose-200',   badge: 'bg-rose-100 text-rose-700',   title: 'text-rose-800' },
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', badge: 'bg-indigo-100 text-indigo-700', title: 'text-indigo-800' },
  gray:   { bg: 'bg-slate-50',  border: 'border-slate-200',  badge: 'bg-slate-200 text-slate-600', title: 'text-slate-700' },
};

const renderHelpSectionIcon = (iconType) => {
  switch (iconType) {
    case 'font': return <FaFont className="text-sm text-blue-600" />;
    case 'layer-group': return <FaLayerGroup className="text-sm text-violet-600" />;
    case 'lock': return <FaLock className="text-sm text-amber-600" />;
    case 'tag': return <FaTag className="text-sm text-emerald-600" />;
    case 'cube': return <FaCube className="text-sm text-teal-600" />;
    case 'key': return <FaKey className="text-sm text-rose-600" />;
    case 'project-diagram': return <FaProjectDiagram className="text-sm text-indigo-600" />;
    case 'warning': return <FaExclamationTriangle className="text-sm text-amber-600" />;
    default: return <FaInfoCircle className="text-sm text-slate-500" />;
  }
};

function XsdHelpPanel({ onClose }) {
  const [activeSection, setActiveSection] = useState('tipos-dato');
  const section = XSD_HELP_SECTIONS.find((s) => s.id === activeSection) || XSD_HELP_SECTIONS[0];
  const colors = COLOR_MAP[section.color] || COLOR_MAP.gray;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="relative flex h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-primary/5 to-transparent px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Guía del Constructor XSD</h2>
            <p className="mt-0.5 text-xs text-slate-500">Referencia de todas las reglas y validaciones del estándar XSD 1.0</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <FaTimes className="text-sm" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <aside className="w-56 shrink-0 overflow-y-auto border-r border-slate-100 bg-slate-50 p-3">
            {XSD_HELP_SECTIONS.map((s) => {
              const c = COLOR_MAP[s.color] || COLOR_MAP.gray;
              const isActive = s.id === activeSection;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setActiveSection(s.id)}
                  className={`mb-1 flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition ${
                    isActive
                      ? `${c.bg} ${c.title} font-semibold ring-1 ${c.border}`
                      : 'text-slate-600 hover:bg-white hover:text-slate-900'
                  }`}
                >
                  <span className="shrink-0">{renderHelpSectionIcon(s.iconType)}</span>
                  <span>{s.title}</span>
                </button>
              );
            })}
          </aside>

          {/* Content */}
          <main className="flex-1 overflow-y-auto p-6">
            <div className={`mb-5 flex items-center gap-3 rounded-xl border p-4 ${colors.bg} ${colors.border}`}>
              <span className="shrink-0">{renderHelpSectionIcon(section.iconType)}</span>
              <h3 className={`text-base font-bold ${colors.title}`}>{section.title}</h3>
            </div>

            <div className="space-y-3">
              {section.items.map((item, idx) => (
                <div key={idx} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                  <code className={`inline-block rounded-lg px-2 py-0.5 text-xs font-bold ${colors.badge}`}>
                    {item.term}
                  </code>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>

            {section.id === 'fuera-alcance' && (
              <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5">
                  <FaLightbulb className="text-amber-600" /> Solución sugerida
                </p>
                <p className="mt-1 text-sm text-amber-700">
                  Para reglas que XSD no puede expresar, expón un hook para reglas Schematron
                  o implementa post-procesamiento con código personalizado en el motor de validación.
                </p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

// XSD test examples modal.
function XsdExamplesModal({ onSelect, onClose }) {
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 bg-amber-50/60 px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
              <FaFlask />
            </span>
            <div>
              <h2 className="text-base font-bold text-slate-900">Ejemplos de Prueba XSD</h2>
              <p className="text-xs text-slate-500">Carga esquemas completos listos para probar y validar</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <FaTimes className="text-sm" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {XSD_TEST_EXAMPLES.map((example) => (
            <div
              key={example.id}
              className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 transition hover:border-amber-300 hover:bg-amber-50/30"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-sm font-bold text-slate-900">{example.name}</h3>
                  <p className="mt-1 text-xs text-slate-600">{example.description}</p>
                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <span className="rounded-md bg-white px-2 py-0.5 border border-slate-200 font-mono text-[11px] text-teal-700">
                      {example.targetNamespace}
                    </span>
                    <span className="rounded-md bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                      Raíz: {example.structure?.[0]?.name}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onSelect(example)}
                  className="shrink-0 rounded-xl bg-amber-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-amber-700"
                >
                  Cargar ejemplo
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Facets section.
function FacetsSection({ field, fieldIndex, currentPath, updateField, expanded, onToggle }) {
  const [enumInput, setEnumInput] = useState('');
  const [testRegexInput, setTestRegexInput] = useState('');

  const applicableFacets = getApplicableFacets(field.type);
  if (applicableFacets.length === 0) return null;

  const addEnumValue = () => {
    const val = enumInput.trim();
    if (!val) return;
    const current = field.enumValues || [];
    if (current.includes(val)) return;
    updateField(currentPath, fieldIndex, { enumValues: [...current, val] });
    setEnumInput('');
  };

  const removeEnumValue = (v) => {
    updateField(currentPath, fieldIndex, {
      enumValues: (field.enumValues || []).filter((x) => x !== v),
    });
  };

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/50">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700"
      >
        <span className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-xs text-primary">
            {applicableFacets.length}
          </span>
          Facets de restricción
        </span>
        {expanded ? <FaChevronUp className="text-xs text-slate-400" /> : <FaChevronDown className="text-xs text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-200 p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {applicableFacets.map((facetKey) => {
              const meta = FACET_LABELS[facetKey];
              if (!meta) return null;

              if (facetKey === 'enumeration') {
                return (
                  <div key={facetKey} className="sm:col-span-2">
                    <label className={LABEL_CLASS}>{meta.label}</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className={`${INPUT_CLASS} flex-1`}
                        value={enumInput}
                        onChange={(e) => setEnumInput(e.target.value)}
                        placeholder={meta.placeholder}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEnumValue(); } }}
                      />
                      <button
                        type="button"
                        onClick={addEnumValue}
                        className="rounded-xl border border-primary/30 bg-white px-3 text-sm font-semibold text-primary hover:bg-primary/5"
                      >
                        +
                      </button>
                    </div>
                    {(field.enumValues || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(field.enumValues || []).map((v) => (
                          <span key={v} className="flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                            {v}
                            <button type="button" onClick={() => removeEnumValue(v)} className="text-primary/60 hover:text-primary">
                              <FaTimes className="text-[10px]" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              if (meta.type === 'enum' && meta.options) {
                return (
                  <div key={facetKey}>
                    <label className={LABEL_CLASS}>{meta.label}</label>
                    <select
                      className={INPUT_CLASS}
                      value={field[facetKey] || ''}
                      onChange={(e) => updateField(currentPath, fieldIndex, { [facetKey]: e.target.value || null })}
                    >
                      <option value="">— Sin restricción —</option>
                      {meta.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                );
              }

              if (meta.type === 'integer') {
                return (
                  <div key={facetKey}>
                    <label className={LABEL_CLASS}>{meta.label}</label>
                    <input
                      type="number"
                      min="0"
                      className={INPUT_CLASS}
                      value={field[facetKey] ?? ''}
                      onChange={(e) => {
                        const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                        updateField(currentPath, fieldIndex, { [facetKey]: v });
                      }}
                      placeholder={meta.placeholder}
                    />
                  </div>
                );
              }

              if (facetKey === 'pattern') {
                let testStatus = null;
                const patternToTest = field.pattern || field.regex;
                if (patternToTest && testRegexInput) {
                  try {
                    const reg = new RegExp(patternToTest);
                    testStatus = reg.test(testRegexInput) ? 'match' : 'no_match';
                  } catch (e) {
                    testStatus = 'invalid_regex';
                  }
                }

                return (
                  <div key={facetKey} className="sm:col-span-2">
                    <label className={LABEL_CLASS}>{meta.label}</label>
                    <input
                      type="text"
                      className={INPUT_CLASS}
                      value={field.pattern || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateField(currentPath, fieldIndex, { pattern: val, regex: val });
                      }}
                      placeholder={meta.placeholder}
                    />
                    {/* Live regex tester. */}
                    <div className="mt-2 rounded-xl border border-slate-200 bg-white p-3 shadow-xs">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-slate-600">Probar patrón en tiempo real:</span>
                        {testRegexInput && testStatus === 'match' && (
                          <span className="flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700 border border-emerald-200">
                            <FaCheck className="text-[10px]" /> Cumple el patrón
                          </span>
                        )}
                        {testRegexInput && testStatus === 'no_match' && (
                          <span className="flex items-center gap-1 rounded-md bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700 border border-rose-200">
                            <FaTimes className="text-[10px]" /> NO cumple el patrón
                          </span>
                        )}
                        {testRegexInput && testStatus === 'invalid_regex' && (
                          <span className="rounded-md bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 border border-amber-200">
                            Sintaxis regex inválida
                          </span>
                        )}
                      </div>
                      <input
                        type="text"
                        className="box-border w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 outline-none transition focus:border-primary focus:bg-white"
                        placeholder="Escribe un valor de muestra para verificar (ej. CURP, folio, código)..."
                        value={testRegexInput}
                        onChange={(e) => setTestRegexInput(e.target.value)}
                      />
                    </div>
                  </div>
                );
              }

              // text
              return (
                <div key={facetKey}>
                  <label className={LABEL_CLASS}>{meta.label}</label>
                  <input
                    type="text"
                    className={INPUT_CLASS}
                    value={field[facetKey] || ''}
                    onChange={(e) => updateField(currentPath, fieldIndex, { [facetKey]: e.target.value })}
                    placeholder={meta.placeholder}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Constraints section.
function ConstraintsSection({ constraints, onChange }) {
  const add = () => onChange([...(constraints || []), createEmptyConstraint()]);

  const update = (idx, patch) => {
    const next = [...(constraints || [])];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const remove = (idx) => onChange((constraints || []).filter((_, i) => i !== idx));

  const addField = (idx) => {
    const next = [...(constraints || [])];
    next[idx] = { ...next[idx], fields: [...(next[idx].fields || []), ''] };
    onChange(next);
  };

  const updateField = (cIdx, fIdx, val) => {
    const next = [...(constraints || [])];
    const fields = [...(next[cIdx].fields || [])];
    fields[fIdx] = val;
    next[cIdx] = { ...next[cIdx], fields };
    onChange(next);
  };

  const removeField = (cIdx, fIdx) => {
    const next = [...(constraints || [])];
    next[cIdx] = { ...next[cIdx], fields: (next[cIdx].fields || []).filter((_, i) => i !== fIdx) };
    onChange(next);
  };

  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50/30">
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-rose-800"
      >
        <span className="flex items-center gap-2">
          <FaLink className="text-rose-600 text-xs" />
          Integridad referencial ({(constraints || []).length} constraint{(constraints || []).length !== 1 ? 's' : ''})
        </span>
        {expanded ? <FaChevronUp className="text-xs text-rose-400" /> : <FaChevronDown className="text-xs text-rose-400" />}
      </button>

      {expanded && (
        <div className="border-t border-rose-200 p-4 space-y-4">
          {(constraints || []).map((c, cIdx) => (
            <div key={cIdx} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Constraint {cIdx + 1}</span>
                <button type="button" onClick={() => remove(cIdx)} className="flex h-7 w-7 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-500 hover:bg-red-100">
                  <FaTimes className="text-[10px]" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={LABEL_CLASS}>Nombre</label>
                  <input type="text" className={INPUT_CLASS} value={c.name || ''} onChange={(e) => update(cIdx, { name: e.target.value })} placeholder="unicidadFolio" />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Tipo</label>
                  <Select
                    options={CONSTRAINT_KIND_OPTIONS}
                    value={CONSTRAINT_KIND_OPTIONS.find((o) => o.value === c.kind) || null}
                    onChange={(opt) => update(cIdx, { kind: opt?.value || 'unique' })}
                    menuPortalTarget={document.body}
                    styles={SELECT_STYLES}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={LABEL_CLASS}>Selector XPath</label>
                  <input type="text" className={INPUT_CLASS} value={c.selector || ''} onChange={(e) => update(cIdx, { selector: e.target.value })} placeholder="Facturas/Factura" />
                </div>

                {c.kind === 'keyref' && (
                  <div className="sm:col-span-2">
                    <label className={LABEL_CLASS}>Refer (nombre del key referenciado)</label>
                    <input type="text" className={INPUT_CLASS} value={c.refer || ''} onChange={(e) => update(cIdx, { refer: e.target.value })} placeholder="nombreDelKey" />
                  </div>
                )}
              </div>

              <div className="mt-3">
                <label className={LABEL_CLASS}>Campos XPath</label>
                {(c.fields || []).map((f, fIdx) => (
                  <div key={fIdx} className="mb-2 flex gap-2">
                    <input type="text" className={`${INPUT_CLASS} flex-1`} value={f} onChange={(e) => updateField(cIdx, fIdx, e.target.value)} placeholder="@id o ./Nombre" />
                    <button type="button" onClick={() => removeField(cIdx, fIdx)} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-red-200 bg-red-50 text-red-500 hover:bg-red-100">
                      <FaTimes className="text-xs" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => addField(cIdx)} className="mt-1 text-xs font-semibold text-primary hover:underline">
                  + Agregar campo XPath
                </button>
              </div>
            </div>
          ))}

          <button type="button" onClick={add} className="w-full rounded-xl border border-dashed border-rose-300 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50">
            + Agregar constraint
          </button>
        </div>
      )}
    </div>
  );
}

// Publication confirmation modal.
function PublishConfirmModal({ draft, onConfirm, onCancel }) {
  const countStats = (nodes) => {
    let n = 0;
    let f = 0;
    const walk = (list) => {
      for (const node of list || []) {
        n += 1;
        f += (node.items || []).length;
        const children = Array.isArray(node.childrens)
          ? node.childrens
          : (Array.isArray(node.children) ? node.children : []);
        if (children.length) walk(children);
      }
    };
    walk(nodes);
    return { nodeCount: n, fieldCount: f };
  };

  const { nodeCount, fieldCount } = countStats(draft.structure || []);
  const hasEffectiveDate = Boolean(draft.effectiveDate);
  const formattedEffectiveDate = hasEffectiveDate
    ? new Date(draft.effectiveDate).toLocaleString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl border border-slate-200">
        <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
            <FaShieldAlt className="text-2xl" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">Confirmar publicación de XSD</h3>
            <p className="text-xs text-slate-500">Esta versión pasará a estado oficial e inmutable</p>
          </div>
        </div>

        <div className="my-5 space-y-4">
          <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3.5 text-xs text-amber-800">
            <div className="flex items-start gap-2">
              <FaExclamationTriangle className="text-sm shrink-0 mt-0.5 text-amber-600" />
              <div>
                <strong className="font-bold">Regla de inmutabilidad y trazabilidad:</strong>
                <p className="mt-1">
                  Una vez publicada la versión <strong>{draft.version || '1.0'}</strong>, quedará registrada y bloqueada contra modificaciones directas. Todo cambio posterior requerirá generar una nueva versión.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm space-y-2.5">
            <div className="flex justify-between items-center text-slate-700">
              <span className="font-medium text-slate-500">Versión a publicar:</span>
              <span className="font-bold text-primary font-mono text-base">{draft.version || '1.0'}</span>
            </div>
            <div className="flex justify-between items-center text-slate-700">
              <span className="font-medium text-slate-500">Estructura validada:</span>
              <span className="font-semibold text-slate-800">{nodeCount} nodo(s), {fieldCount} campo(s)</span>
            </div>
            <div className="flex justify-between items-center text-slate-700">
              <span className="font-medium text-slate-500">Entrada en vigor:</span>
              <span className="font-semibold text-slate-800">
                {hasEffectiveDate ? (
                  <span className="inline-flex items-center gap-1.5 text-blue-700 font-bold">
                    <FaClock className="text-xs" /> {formattedEffectiveDate}
                  </span>
                ) : (
                  <span className="text-emerald-700 font-bold">Inmediata al publicar</span>
                )}
              </span>
            </div>
            {draft.targetNamespace && (
              <div className="flex justify-between items-center text-slate-700">
                <span className="font-medium text-slate-500">Namespace:</span>
                <span className="font-mono text-xs text-slate-600 truncate max-w-[240px]">{draft.targetNamespace}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
          >
            Volver a editar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-emerald-700 hover:shadow-md"
          >
            <FaCheck className="text-xs" />
            Sí, validar y publicar
          </button>
        </div>
      </div>
    </div>
  );
}

// Interoperability import modal.
function InteropImportModal({ catalog, loading, onImport, onClose }) {
  const [selectedServiceIdx, setSelectedServiceIdx] = useState(0);
  const [selectedFieldKeys, setSelectedFieldKeys] = useState(new Set());
  const [importKind, setImportKind] = useState('ATTRIBUTE');
  const [search, setSearch] = useState('');

  const currentService = catalog[selectedServiceIdx] || null;

  useEffect(() => {
    if (currentService?.fields) {
      setSelectedFieldKeys(new Set(currentService.fields.map((f) => f.sourceFieldName)));
    }
  }, [selectedServiceIdx, currentService]);

  const filteredFields = (currentService?.fields || []).filter((f) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      f.sourceFieldName?.toLowerCase().includes(q) ||
      f.targetFieldName?.toLowerCase().includes(q) ||
      f.label?.toLowerCase().includes(q) ||
      f.jsonPath?.toLowerCase().includes(q)
    );
  });

  const toggleFieldKey = (key) => {
    setSelectedFieldKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = (all) => {
    if (all && currentService?.fields) {
      setSelectedFieldKeys(new Set(currentService.fields.map((f) => f.sourceFieldName)));
    } else {
      setSelectedFieldKeys(new Set());
    }
  };

  const handleConfirm = () => {
    if (!currentService?.fields) return;
    const selected = currentService.fields.filter((f) => selectedFieldKeys.has(f.sourceFieldName));
    if (selected.length === 0) {
      toast.error('Selecciona al menos un campo para importar');
      return;
    }
    onImport(selected, importKind);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden border border-slate-200">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-100 text-teal-700">
              <FaNetworkWired className="text-base" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800 m-0">
                Importar Campos de Interoperabilidad
              </h3>
              <p className="text-xs text-slate-500 m-0">
                Selecciona campos preconfigurados y filtrados de dependencias para insertarlos en el nodo XSD
              </p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition">
            <FaTimes className="text-base" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col overflow-y-auto p-5 gap-4 bg-slate-50/50">
          {loading ? (
            <div className="py-12 text-center text-slate-500 text-sm">
              <FaSpinner className="animate-spin inline mr-2 text-primary" /> Cargando catálogo de interoperability...
            </div>
          ) : catalog.length === 0 ? (
            <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/80 p-6 text-center">
              <FaExclamationTriangle className="text-amber-500 text-2xl mx-auto mb-2" />
              <p className="text-sm font-bold text-amber-900 m-0">No hay campos de interoperability guardados</p>
              <p className="text-xs text-amber-700 mt-1">
                Ve al módulo de <strong>Interoperabilidad</strong>, realiza una prueba de conexión en cualquier conector (SEP, Registro Civil, RENAPO, SAT) y haz clic en <em>"Guardar Selección para XSD"</em>.
              </p>
            </div>
          ) : (
            <>
              {/* Service selector. */}
              <div>
                <label className="text-xs font-bold text-slate-700 mb-1.5 block">
                  1. Selecciona la Dependencia / Fuente de Datos:
                </label>
                <div className="flex flex-wrap gap-2">
                  {catalog.map((srv, idx) => (
                    <button
                      key={srv.serviceCode}
                      type="button"
                      onClick={() => setSelectedServiceIdx(idx)}
                      className={`rounded-xl border px-3.5 py-2 text-xs font-semibold transition text-left ${
                        selectedServiceIdx === idx
                          ? 'border-teal-500 bg-teal-50 text-teal-900 shadow-xs'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="font-bold">{srv.serviceName}</div>
                      <div className="text-[11px] font-normal text-slate-500">{srv.fieldCount} campos disponibles</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Import and search mode. */}
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3">
                <div className="flex items-center gap-3">
                  <label className="text-xs font-bold text-slate-700">2. Importar como:</label>
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <input
                      type="radio"
                      name="importKind"
                      value="ATTRIBUTE"
                      checked={importKind === 'ATTRIBUTE'}
                      onChange={() => setImportKind('ATTRIBUTE')}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span className="font-semibold text-slate-700">Atributo XML (xs:attribute)</span>
                  </label>
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <input
                      type="radio"
                      name="importKind"
                      value="ELEMENT"
                      checked={importKind === 'ELEMENT'}
                      onChange={() => setImportKind('ELEMENT')}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span className="font-semibold text-slate-700">Elemento XML (xs:element)</span>
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => selectAll(true)}
                    className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-100"
                  >
                    Todos
                  </button>
                  <button
                    type="button"
                    onClick={() => selectAll(false)}
                    className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-100"
                  >
                    Ninguno
                  </button>
                </div>
              </div>

              {/* Service field list. */}
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-2xs">
                <div className="border-b border-slate-100 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 flex justify-between">
                  <span>Campos Disponibles ({filteredFields.length})</span>
                  <span>{selectedFieldKeys.size} seleccionados</span>
                </div>
                <div className="max-h-[260px] overflow-y-auto divide-y divide-slate-100">
                  {filteredFields.map((f) => (
                    <label
                      key={f.sourceFieldName}
                      className="flex items-center justify-between gap-3 px-3.5 py-2 hover:bg-teal-50/40 cursor-pointer transition text-xs"
                    >
                      <div className="flex items-center gap-2.5">
                        <input
                          type="checkbox"
                          checked={selectedFieldKeys.has(f.sourceFieldName)}
                          onChange={() => toggleFieldKey(f.sourceFieldName)}
                          className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 h-4 w-4 cursor-pointer"
                        />
                        <div>
                          <span className="font-bold text-slate-800">{f.targetFieldName || f.sourceFieldName}</span>
                          {f.label && f.label !== f.targetFieldName && (
                            <span className="text-slate-400 ml-1.5 font-normal">({f.label})</span>
                          )}
                          {f.jsonPath && (
                            <span className="font-mono text-[10px] text-slate-400 block">{f.jsonPath}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <span className="rounded bg-slate-100 font-mono text-[10px] text-slate-700 px-2 py-0.5">
                          xs:{f.xsdType || 'string'}
                        </span>
                        {f.sampleValue && (
                          <span className="font-mono text-[10px] text-slate-500 max-w-[140px] truncate" title={f.sampleValue}>
                            {f.sampleValue}
                          </span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-200 bg-white px-6 py-3.5">
          <button type="button" onClick={onClose} className="btn-cancel">
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={catalog.length === 0 || selectedFieldKeys.size === 0}
            className="btn-primary flex items-center gap-2"
          >
            <FaSave />
            Importar {selectedFieldKeys.size} campo{selectedFieldKeys.size === 1 ? '' : 's'} al Nodo
          </button>
        </div>
      </div>
    </div>
  );
}

// Main component.
function NewXsdVersion({
  entityName = '',
  draft,
  setDraft,
  onSave,
  saveLabel = 'Publicar versión',
  extraForm = null,
  onClose = null,
  previousVersion = null,
  onAutoSave = null,
  isDraftMode = true,
  xsdEvents = [],
}) {
  const { regex, setRegex, fetchRegex } = useRegex();
  const [activeModuleTab, setActiveModuleTab] = useState('structure'); // 'structure' | 'namespace' | 'effectiveDate' | 'constraints' | 'xmlPreview'
  const [copiedXml, setCopiedXml] = useState(false);
  const [expandedChildren, setExpandedChildren] = useState({});
  const [expandedFacets, setExpandedFacets] = useState({});
  const [openModelRegex, setOpenModelRegex] = useState(false);
  const [regexModalTarget, setRegexModalTarget] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showExamplesModal, setShowExamplesModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ effectiveDate: true, namespace: false });

  // Interoperabilidad
  const [showInteropModal, setShowInteropModal] = useState(false);
  const [interopTargetNodePath, setInteropTargetNodePath] = useState(null);
  const [interopCatalog, setInteropCatalog] = useState([]);
  const [loadingInteropCatalog, setLoadingInteropCatalog] = useState(false);

  // Version source: 'previous' (copy the previous version) or 'blank' (start from scratch).
  const [originMode, setOriginMode] = useState(previousVersion ? 'previous' : 'blank');

  // Smart auto-save state.
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'pending' | 'saving' | 'saved' | 'error'
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const isSavingRef = useRef(false);
  const lastSavedSignatureRef = useRef(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const openInteropModalForNode = async (nodePath) => {
    setInteropTargetNodePath(nodePath);
    setShowInteropModal(true);
    setLoadingInteropCatalog(true);
    try {
      const data = await interoperabilityApi.getFieldsCatalog();
      setInteropCatalog(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Error al cargar campos de interoperability: ' + err.message);
    } finally {
      setLoadingInteropCatalog(false);
    }
  };

  const handleImportInteropFields = (selectedFields, importKind = 'ATTRIBUTE') => {
    if (!interopTargetNodePath) return;
    const typeMap = {
      string: 'xs:string',
      integer: 'xs:integer',
      decimal: 'xs:decimal',
      date: 'xs:date',
      dateTime: 'xs:dateTime',
      boolean: 'xs:boolean',
    };

    setDraft((prev) => {
      const updated = cloneNodeTree(prev.structure || []);
      const node = getNodeAtPath(updated, interopTargetNodePath);
      if (!node) return prev;

      const newItems = selectedFields.map((f) => {
        const rawType = (f.xsdType || 'string').toLowerCase();
        const mappedType = typeMap[rawType] || (f.xsdType?.startsWith('xs:') ? f.xsdType : 'xs:string');
        return {
          ...createEmptyField(),
          itemName: f.targetFieldName || f.sourceFieldName,
          nodeKind: importKind,
          type: mappedType,
          use: f.isRequired ? 'required' : 'optional',
          defaultValue: f.defaultValue || '',
        };
      });

      node.items = [...(node.items || []), ...newItems];
      return { ...prev, structure: updated };
    });

    toast.success(`${selectedFields.length} campos importados de interoperability`);
    setShowInteropModal(false);
  };

  useEffect(() => { fetchRegex(); }, []);

  // Initialize the signature of the state currently saved in the database.
  useEffect(() => {
    if (draft && isDraftMode) {
      try {
        lastSavedSignatureRef.current = JSON.stringify(serializeVersionForPayload(draft));
      } catch {
        // ignore
      }
    }
  }, []);

  // Auto-save only when the draft differs from the state saved in the database.
  useEffect(() => {
    if (!onAutoSave || !isDraftMode) return;
    if (!draft?.version?.trim()) return;

    let currentSignature;
    try {
      currentSignature = JSON.stringify(serializeVersionForPayload(draft));
    } catch {
      return;
    }

    // Initialize the signature on first use.
    if (lastSavedSignatureRef.current === null) {
      lastSavedSignatureRef.current = currentSignature;
      return;
    }

    // Skip auto-save when the draft matches the state saved in the database.
    if (currentSignature === lastSavedSignatureRef.current) {
      if (saveStatus === 'pending') {
        setSaveStatus('saved');
      }
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return;
    }

    // There are unsaved changes.
    setSaveStatus('pending');
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

    autoSaveTimerRef.current = setTimeout(async () => {
      if (isSavingRef.current) return;

      const payloadToSave = serializeVersionForPayload(draftRef.current);
      const signatureToSave = JSON.stringify(payloadToSave);

      // Skip the save if the draft matches the saved state when the timer expires.
      if (signatureToSave === lastSavedSignatureRef.current) {
        setSaveStatus('saved');
        return;
      }

      isSavingRef.current = true;
      setSaveStatus('saving');
      try {
        await onAutoSave(draftRef.current);
        lastSavedSignatureRef.current = signatureToSave;
        setSaveStatus('saved');
        const now = new Date();
        const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        setLastSavedTime(timeStr);
      } catch (err) {
        setSaveStatus('error');
      } finally {
        isSavingRef.current = false;
      }
    }, 3000);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [draft, onAutoSave, isDraftMode]);

  const triggerManualDraftSave = async () => {
    if (!onAutoSave) return;
    if (!draft?.version?.trim()) {
      toast.error('Debe ingresar un nombre o número de versión');
      return;
    }

    let currentSignature;
    try {
      currentSignature = JSON.stringify(serializeVersionForPayload(draft));
    } catch {
      currentSignature = null;
    }

    // Avoid an unnecessary save when there are no changes.
    if (currentSignature && currentSignature === lastSavedSignatureRef.current) {
      toast.info('No hay cambios pendientes por guardar en el borrador');
      return;
    }

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    isSavingRef.current = true;
    setSaveStatus('saving');
    try {
      await onAutoSave(draft);
      if (currentSignature) {
        lastSavedSignatureRef.current = currentSignature;
      }
      setSaveStatus('saved');
      const now = new Date();
      const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setLastSavedTime(timeStr);
      toast.success('Borrador guardado en la base de datos');
    } catch (err) {
      setSaveStatus('error');
      toast.error('Error al guardar borrador: ' + (err.message || ''));
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleOriginChange = (mode) => {
    setOriginMode(mode);
    if (mode === 'previous' && previousVersion) {
      setDraft((prev) => ({
        ...prev,
        targetNamespace: previousVersion.targetNamespace || '',
        elementFormDefault: previousVersion.elementFormDefault || 'unqualified',
        attributeFormDefault: previousVersion.attributeFormDefault || 'unqualified',
        structure: cloneNodeTree(previousVersion.structure || previousVersion.estructura || []),
      }));
      toast.success(`Estructura copiada de la versión anterior (${previousVersion.version})`);
    } else if (mode === 'blank') {
      setDraft((prev) => ({
        ...prev,
        structure: [createEmptyNode()],
      }));
      toast('Lienzo en blanco inicializado desde cero');
    }
  };

  const countMetrics = (nodes = []) => {
    let nodeCount = 0;
    let elemCount = 0;
    let attrCount = 0;
    let constraintCount = 0;

    const traverse = (nodeList) => {
      if (!Array.isArray(nodeList)) return;
      nodeCount += nodeList.length;
      nodeList.forEach((n) => {
        if (Array.isArray(n.items)) {
          n.items.forEach((item) => {
            if (item.nodeKind === 'ATTRIBUTE') attrCount++;
            else elemCount++;
          });
        }
        if (Array.isArray(n.constraints)) {
          constraintCount += n.constraints.length;
        }
        const children = Array.isArray(n.childrens)
          ? n.childrens
          : (Array.isArray(n.children) ? n.children : []);
        if (children.length) {
          traverse(children);
        }
      });
    };
    traverse(nodes);
    return { nodeCount, elemCount, attrCount, constraintCount };
  };

  const metrics = countMetrics(draft.structure || []);
  const currentXmlPreview = generateXsdXmlPreview(draft);

  const handleCopyXml = () => {
    navigator.clipboard.writeText(currentXmlPreview);
    setCopiedXml(true);
    toast.success('Esquema XSD copiado al portapapeles');
    setTimeout(() => setCopiedXml(false), 2000);
  };

  const handleDownloadXsd = () => {
    const blob = new Blob([currentXmlPreview], { type: 'application/xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `schema_${draft.version || 'v1.0.0'}.xsd`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Archivo .xsd descargado exitosamente');
  };

  // Tree helpers.

  const cloneNodeTree = (value) => JSON.parse(JSON.stringify(value ?? []));

  const updateDraft = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

  const getNodeAtPath = (structure, path) => {
    let currentArray = structure;
    let currentNode = null;
    for (let i = 0; i < path.length; i++) {
      currentNode = currentArray[path[i]];
      if (!currentNode) return null;
      if (i < path.length - 1) {
        currentArray = Array.isArray(currentNode.childrens)
          ? currentNode.childrens
          : (Array.isArray(currentNode.children) ? currentNode.children : []);
      }
    }
    return currentNode;
  };

  const getParentArrayAtPath = (structure, path) => {
    if (!path.length) return structure;
    let currentArray = structure;
    for (let i = 0; i < path.length - 1; i++) {
      const node = currentArray[path[i]];
      currentArray = Array.isArray(node?.childrens)
        ? node.childrens
        : (Array.isArray(node?.children) ? node.children : []);
    }
    return currentArray;
  };

  const toggleSection = (key) => setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Node operations.

  const updateNodeDraft = (draftValue, nodePath, field, value) => {
    const updated = cloneNodeTree(draftValue.structure || []);
    const node = getNodeAtPath(updated, nodePath);
    if (!node) return draftValue;
    node[field] = value;
    return { ...draftValue, structure: updated };
  };

  const updateNodeInDraft = (nodePath, field, value) =>
    setDraft((prev) => updateNodeDraft(prev, nodePath, field, value));

  const updateNodeMulti = (nodePath, patch) =>
    setDraft((prev) => {
      const updated = cloneNodeTree(prev.structure || []);
      const node = getNodeAtPath(updated, nodePath);
      if (!node) return prev;
      Object.assign(node, patch);
      return { ...prev, structure: updated };
    });

  // Field operations.

  const addFieldToNodeDraft = (draftValue, nodePath) => {
    const updated = cloneNodeTree(draftValue.structure || []);
    const node = getNodeAtPath(updated, nodePath);
    if (!node) return draftValue;
    node.items = [...(node.items || []), createEmptyField()];
    return { ...draftValue, structure: updated };
  };

  const removeFieldFromNodeDraft = (draftValue, nodePath, fieldIndex) => {
    const updated = cloneNodeTree(draftValue.structure || []);
    const node = getNodeAtPath(updated, nodePath);
    if (!node) return draftValue;
    node.items = (node.items || []).filter((_, i) => i !== fieldIndex);
    return { ...draftValue, structure: updated };
  };

  const updateField = (currentPath, fieldIndex, changes) => {
    setDraft((prev) => {
      const updated = cloneNodeTree(prev.structure || []);
      const node = getNodeAtPath(updated, currentPath);
      if (!node || !Array.isArray(node.items) || !node.items[fieldIndex]) return prev;
      node.items[fieldIndex] = { ...node.items[fieldIndex], ...changes };
      return { ...prev, structure: updated };
    });
  };

  // Child node operations.

  const addChildNodeDraft = (draftValue, nodePath) => {
    const updated = cloneNodeTree(draftValue.structure || []);
    const node = getNodeAtPath(updated, nodePath);
    if (!node) return draftValue;
    const currentChildren = Array.isArray(node.childrens)
      ? node.childrens
      : (Array.isArray(node.children) ? node.children : []);
    const newChildren = [...currentChildren, createEmptyNode()];
    node.childrens = newChildren;
    node.children = newChildren;
    return { ...draftValue, structure: updated };
  };

  const toggleChildren = (currentPath) => {
    const key = currentPath.join('-');
    setExpandedChildren((prev) => {
      const currentVal = prev[key] !== undefined ? prev[key] : true;
      return { ...prev, [key]: !currentVal };
    });
  };

  const removeNodeFromDraft = (draftValue, nodePath) => {
    const updated = cloneNodeTree(draftValue.structure || []);
    const parentArray = getParentArrayAtPath(updated, nodePath);
    const currentIndex = nodePath[nodePath.length - 1];
    if (parentArray.length === 0 || currentIndex === undefined) return draftValue;
    const nextArray = parentArray.filter((_, i) => i !== currentIndex);
    if (nodePath.length === 1) {
      return { ...draftValue, structure: nextArray.length ? nextArray : [createEmptyNode()] };
    }
    const parentNode = getNodeAtPath(updated, nodePath.slice(0, -1));
    if (parentNode) {
      parentNode.childrens = nextArray;
      parentNode.children = nextArray;
    }
    return { ...draftValue, structure: updated };
  };

  // Regex ──

  const nextOnSaved = (data, currentPath, fieldIndex) => {
    setRegex((prev) => [...prev, data]);
    updateField(currentPath, fieldIndex, {
      regexPreset: data?.name || '',
      regex:       data?.regex || '',
      pattern:     data?.regex || '',
      regexPresetId: data?.id || '',
    });
    setOpenModelRegex(false);
    setRegexModalTarget(null);
  };

  // Validation.

  const validateNodeTree = (nodes, path = []) => {
    if (!Array.isArray(nodes)) return { valid: false, path, message: 'La estructura no es válida.' };

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const currentPath = [...path, i];
      const hasFields   = Array.isArray(node?.items) && node.items.length > 0;
      const children = Array.isArray(node?.childrens)
        ? node.childrens
        : (Array.isArray(node?.children) ? node.children : []);
      const hasChildren = children.length > 0;

      if (!hasFields && !hasChildren) {
        return {
          valid: false, path: currentPath,
          message: `El nodo ${currentPath.map((x) => x + 1).join(' → ')} debe tener al menos un campo o un nodo hijo.`,
        };
      }

      // Node cardinality.
      if (hasFields || hasChildren) {
        const cardResult = validateCardinality(node.minOccurs ?? 1, node.maxOccurs ?? '1', `Nodo ${currentPath.map((x) => x + 1).join(' → ')}`);
        if (!cardResult.valid) return { valid: false, path: currentPath, message: cardResult.message };
      }

      // In the 'all' compositor, child nodes must have maxOccurs=1.
      if (node.compositor === 'all' && hasChildren) {
        for (let ci = 0; ci < children.length; ci++) {
          const child = children[ci];
          if (child.maxOccurs && child.maxOccurs !== '1') {
            return {
              valid: false, path: [...currentPath, ci],
              message: `Compositor "all": el nodo hijo ${ci + 1} de "${node.name}" debe tener maxOccurs=1 (XSD 1.0).`,
            };
          }
        }
      }

      if (hasFields) {
        for (let fi = 0; fi < node.items.length; fi++) {
          const field = node.items[fi];
          if (!field?.itemName?.trim()) {
            return { valid: false, path: currentPath, fieldIndex: fi, message: `El nombre del campo ${fi + 1} es obligatorio.` };
          }
          if (!field?.type) {
            return { valid: false, path: currentPath, fieldIndex: fi, message: `El tipo de dato del campo "${field.itemName}" es obligatorio.` };
          }
          // Cross-field facets.
          const facetResult = validateFieldFacets(field);
          if (!facetResult.valid) return { valid: false, path: currentPath, fieldIndex: fi, message: facetResult.message };
          // ELEMENT field cardinality.
          if (field.nodeKind === 'ELEMENT') {
            const cr = validateCardinality(field.minOccurs ?? 1, field.maxOccurs ?? '1', `Campo "${field.itemName}"`);
            if (!cr.valid) return { valid: false, path: currentPath, fieldIndex: fi, message: cr.message };
          }
          // Constraints
          if (Array.isArray(node.constraints)) {
            for (let ci = 0; ci < node.constraints.length; ci++) {
              const c = node.constraints[ci];
              if (!c.name?.trim()) return { valid: false, path: currentPath, message: `Constraint ${ci + 1}: debe tener nombre.` };
              if (!c.selector?.trim()) return { valid: false, path: currentPath, message: `Constraint "${c.name}": debe tener selector XPath.` };
              if (!c.fields?.length || !c.fields[0]?.trim()) return { valid: false, path: currentPath, message: `Constraint "${c.name}": debe tener al menos un campo XPath.` };
              if (c.kind === 'keyref' && !c.refer?.trim()) return { valid: false, path: currentPath, message: `Keyref "${c.name}": debe especificar el key referenciado.` };
            }
          }
        }
      }

      if (hasChildren) {
        const r = validateNodeTree(children, currentPath);
        if (!r.valid) return r;
      }
    }
    return { valid: true, path: null };
  };

  const handleSaveDraftManual = async () => {
    await triggerManualDraftSave();
  };

  const handleRequestPublish = () => {
    const validation = validateNodeTree(draft.structure || []);
    if (!validation.valid) {
      toast.error(validation.message, { id: 'xsd-validation-error' });
      return;
    }
    setShowPublishModal(true);
  };

  const handleConfirmPublish = async () => {
    setShowPublishModal(false);
    if (onSave) {
      await onSave({ ...draft, status: 'PUBLISHED' });
    }
  };

  // RENDER FIELD
  const renderField = (field, fieldIndex, currentPath) => {
    const childrenKey = currentPath.join('-');
    const fieldKey    = `${childrenKey}-field-${fieldIndex}`;
    const facetsKey   = `${fieldKey}-facets`;

    const isAttribute = (field.nodeKind || 'ATTRIBUTE') !== 'ELEMENT';

    const regexOptions = [
      { id: '', name: '', label: 'Selecciona tipo de regex', value: '', regex: '' },
      ...regex.map((item) => ({ ...item, label: item.name, value: item.id })),
      { id: 'otro', name: 'otro', label: 'Otro', value: 'otro', regex: '' },
    ];
    const selectedRegex = regexOptions.find((o) => o.id === field.regexPresetId) || null;
    const isRegexModalOpen =
      openModelRegex &&
      regexModalTarget?.fieldIndex === fieldIndex &&
      regexModalTarget?.path?.join('-') === currentPath.join('-');

    return (
      <div key={fieldKey} className="w-full rounded-xl border border-slate-100 bg-slate-50/60 p-4 sm:p-5">
        {/* Field header. */}
        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm font-bold text-slate-700">Campo {fieldIndex + 1}</span>
          <button
            type="button"
            onClick={() => setDraft((prev) => removeFieldFromNodeDraft(prev, currentPath, fieldIndex))}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-red-200 bg-white text-red-500 transition hover:bg-red-50"
          >
            <FaTimes className="text-xs" />
          </button>
        </div>

        {/* Node kind (ATTRIBUTE / ELEMENT). */}
        <div className="mb-4">
          <label className={LABEL_CLASS}>Tipo de nodo XSD</label>
          <div className="flex gap-2">
            {NODE_KIND_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateField(currentPath, fieldIndex, { nodeKind: opt.value })}
                className={`flex-1 rounded-xl border px-3 py-2.5 text-xs font-semibold transition ${
                  (field.nodeKind || 'ATTRIBUTE') === opt.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-primary/30 hover:text-primary'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Name and data type. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={LABEL_CLASS}>Nombre</label>
            <input
              type="text"
              className={INPUT_CLASS}
              value={field.itemName || ''}
              onChange={(e) => {
                justLettersWithoutAccent(e, false);
                updateField(currentPath, fieldIndex, { itemName: e.target.value });
              }}
              placeholder="Nombre del campo"
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Tipo de dato XSD</label>
            <Select
              className="w-full"
              options={typeDataOptions}
              value={
                (() => {
                  for (const group of typeDataOptions) {
                    const found = group.options.find((o) => o.value === field.type);
                    if (found) return found;
                  }
                  return null;
                })()
              }
              onChange={(opt) => updateField(currentPath, fieldIndex, { type: opt?.value || '' })}
              placeholder="Selecciona tipo"
              isSearchable
              menuPortalTarget={document.body}
              styles={SELECT_STYLES}
            />
          </div>
        </div>

        {/* ATTRIBUTE properties. */}
        {isAttribute && (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className={LABEL_CLASS}>use</label>
              <Select
                options={USE_OPTIONS}
                value={USE_OPTIONS.find((o) => o.value === (field.use || 'optional')) || null}
                onChange={(opt) => updateField(currentPath, fieldIndex, { use: opt?.value || 'optional', required: opt?.value === 'required' })}
                menuPortalTarget={document.body}
                styles={SELECT_STYLES}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>default</label>
              <input
                type="text"
                className={INPUT_CLASS}
                value={field.defaultValue || ''}
                disabled={(field.use || 'optional') === 'required'}
                onChange={(e) => updateField(currentPath, fieldIndex, { defaultValue: e.target.value })}
                placeholder={field.use === 'required' ? 'N/A si required' : 'Valor por defecto'}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>fixed</label>
              <input
                type="text"
                className={INPUT_CLASS}
                value={field.fixedValue || ''}
                onChange={(e) => updateField(currentPath, fieldIndex, { fixedValue: e.target.value })}
                placeholder="Valor fijo"
              />
            </div>
          </div>
        )}

        {/* ELEMENT cardinality. */}
        {!isAttribute && (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className={LABEL_CLASS}>minOccurs</label>
              <input
                type="number"
                min="0"
                className={INPUT_CLASS}
                value={field.minOccurs ?? 1}
                onChange={(e) => updateField(currentPath, fieldIndex, { minOccurs: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>maxOccurs</label>
              <input
                type="text"
                className={INPUT_CLASS}
                value={field.maxOccurs || '1'}
                onChange={(e) => updateField(currentPath, fieldIndex, { maxOccurs: e.target.value })}
                placeholder="1 o unbounded"
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                  checked={Boolean(field.nillable)}
                  onChange={(e) => updateField(currentPath, fieldIndex, { nillable: e.target.checked })}
                />
                nillable
              </label>
            </div>
          </div>
        )}

        {/* Regex (legacy) + patrón */}
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={LABEL_CLASS}>Regex (catálogo)</label>
            <Select
              className="w-full"
              options={regexOptions}
              value={selectedRegex}
              onChange={(option) => {
                if (!option) return;
                updateField(currentPath, fieldIndex, {
                  regexPreset:   option.name || '',
                  regex:         option.regex || '',
                  pattern:       option.regex || '',
                  regexPresetId: option.id || '',
                });
                if (option.value === 'otro') {
                  setRegexModalTarget({ path: currentPath, fieldIndex });
                  setOpenModelRegex(true);
                }
              }}
              placeholder="Selecciona un regex"
              isSearchable
              noOptionsMessage={() => 'No se encontraron opciones'}
              menuPortalTarget={document.body}
              styles={SELECT_STYLES}
            />
          </div>
          <div>
            <label className={LABEL_CLASS}>Expresión regular</label>
            {field.regex && field.regexPreset && field.regexPreset !== 'otro' ? (
              <div className="min-h-[46px] overflow-x-auto rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <code className="block whitespace-pre text-xs text-slate-700">{field.regex}</code>
              </div>
            ) : (
              <input
                type="text"
                className={INPUT_CLASS}
                value={field.pattern || ''}
                onChange={(e) => updateField(currentPath, fieldIndex, { pattern: e.target.value, regex: e.target.value })}
                placeholder="ej. [A-Z]{2}[0-9]{6}"
              />
            )}
          </div>
        </div>

        {/* Facets dinámicos */}
        <FacetsSection
          field={field}
          fieldIndex={fieldIndex}
          currentPath={currentPath}
          updateField={updateField}
          expanded={Boolean(expandedFacets[facetsKey])}
          onToggle={() => setExpandedFacets((prev) => ({ ...prev, [facetsKey]: !prev[facetsKey] }))}
        />

        {/* Modal regex */}
        {isRegexModalOpen && (
          <ModalRegex
            onClose={() => { setOpenModelRegex(false); setRegexModalTarget(null); }}
            nextOnSaved={(data) => nextOnSaved(data, currentPath, fieldIndex)}
          />
        )}
      </div>
    );
  };

  // RENDER NODE
  const renderDraftNode = (currentNode, currentPath) => {
    const childrenKey  = currentPath.join('-');
    const rawChildren  = Array.isArray(currentNode.childrens)
      ? currentNode.childrens
      : (Array.isArray(currentNode.children) ? currentNode.children : []);
    const hasChildren  = rawChildren.length > 0;
    // Expand by default so the full tree is immediately visible.
    const showChildren = expandedChildren[childrenKey] !== undefined ? Boolean(expandedChildren[childrenKey]) : true;
    const hasFields    = Array.isArray(currentNode.items) && currentNode.items.length > 0;
    const secConfig    = `${childrenKey}-config`;
    const secWildcard  = `${childrenKey}-wildcard`;

    const needsInheritance = ['simpleContent', 'complexContent'].includes(currentNode.contentModel);

    return (
      <div key={`draft-node-${childrenKey}`} className="w-full rounded-2xl border border-slate-200 bg-white p-3 sm:p-5 lg:p-6 shadow-xs transition">

        {/* Node header. */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary font-bold text-xs sm:text-sm">
              {currentPath.length <= 1 ? <FaSitemap /> : <FaCube />}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-primary">
                  {currentPath.length <= 1
                    ? 'Elemento Raíz'
                    : `Subnodo (Nivel ${currentPath.length - 1}) · #${currentPath[currentPath.length - 1] + 1}`}
                </span>
                <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
                  {currentNode.compositor || 'sequence'}
                </span>
                <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
                  [{currentNode.minOccurs ?? 1}..{currentNode.maxOccurs ?? '1'}]
                </span>
                {hasChildren && (
                  <span className="rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
                    {rawChildren.length} {rawChildren.length === 1 ? 'subnodo' : 'subnodos'}
                  </span>
                )}
                {hasFields && (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700">
                    {currentNode.items.length} {currentNode.items.length === 1 ? 'campo' : 'campos'}
                  </span>
                )}
              </div>
              <h4 className="mt-0.5 text-base sm:text-lg font-bold text-slate-900 break-words">
                {currentNode.name || 'Sin nombre asignado'}
              </h4>
            </div>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {hasChildren && (
              <button
                type="button"
                onClick={() => toggleChildren(currentPath)}
                className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 shadow-2xs hover:bg-slate-50 transition"
                title={showChildren ? 'Colapsar subnodos' : 'Expandir subnodos'}
              >
                {showChildren ? <FaChevronUp className="text-[10px]" /> : <FaChevronDown className="text-[10px]" />}
                <span className="hidden sm:inline">{showChildren ? 'Colapsar hijos' : `Ver hijos (${rawChildren.length})`}</span>
              </button>
            )}
            <button
              type="button"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-red-200 bg-red-50 text-red-500 transition hover:border-red-300 hover:bg-red-100"
              onClick={() => setDraft((prev) => removeNodeFromDraft(prev, currentPath))}
              title="Eliminar este nodo"
            >
              <FaTimes className="text-xs" />
            </button>
          </div>
        </div>

        {/* Node name. */}
        <div className="mb-5 w-full">
          <label className={LABEL_CLASS}>Nombre del nodo</label>
          <input
            type="text"
            className={INPUT_CLASS}
            value={currentNode.name || ''}
            onChange={(e) => {
              justLettersWithoutAccent(e, false);
              updateNodeInDraft(currentPath, 'name', e.target.value);
            }}
            placeholder="Ingresa el nombre del nodo"
          />
        </div>

        {/* Advanced node settings (collapsible). */}
        <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50/40">
          <button
            type="button"
            onClick={() => toggleSection(secConfig)}
            className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700"
          >
            <span className="flex items-center gap-2">
              <FaCog className="text-slate-500 text-xs" />
              Configuración del nodo
            </span>
            {expandedSections[secConfig] ? <FaChevronUp className="text-xs text-slate-400" /> : <FaChevronDown className="text-xs text-slate-400" />}
          </button>

          {expandedSections[secConfig] && (
            <div className="border-t border-slate-200 p-4 space-y-4">
              {/* Content model and compositor. */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={LABEL_CLASS}>Modelo de contenido</label>
                  <Select
                    options={CONTENT_MODEL_OPTIONS}
                    value={CONTENT_MODEL_OPTIONS.find((o) => o.value === (currentNode.contentModel || 'default')) || null}
                    onChange={(opt) => updateNodeInDraft(currentPath, 'contentModel', opt?.value || 'default')}
                    menuPortalTarget={document.body}
                    styles={SELECT_STYLES}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>Compositor de hijos</label>
                  <Select
                    options={COMPOSITOR_OPTIONS}
                    value={COMPOSITOR_OPTIONS.find((o) => o.value === (currentNode.compositor || 'sequence')) || null}
                    onChange={(opt) => updateNodeInDraft(currentPath, 'compositor', opt?.value || 'sequence')}
                    menuPortalTarget={document.body}
                    styles={SELECT_STYLES}
                  />
                </div>
              </div>

              {/* Herencia */}
              {needsInheritance && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={LABEL_CLASS}>Mecanismo de herencia</label>
                    <Select
                      options={INHERITANCE_OPTIONS}
                      value={INHERITANCE_OPTIONS.find((o) => o.value === (currentNode.inheritance || 'extension')) || null}
                      onChange={(opt) => updateNodeInDraft(currentPath, 'inheritance', opt?.value || 'extension')}
                      menuPortalTarget={document.body}
                      styles={SELECT_STYLES}
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLASS}>Tipo base</label>
                    <input
                      type="text"
                      className={INPUT_CLASS}
                      value={currentNode.baseType || ''}
                      onChange={(e) => updateNodeInDraft(currentPath, 'baseType', e.target.value)}
                      placeholder="ej. xs:string o MiTipoBase"
                    />
                  </div>
                </div>
              )}

              {/* Node cardinality. */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={LABEL_CLASS}>minOccurs</label>
                  <input
                    type="number"
                    min="0"
                    className={INPUT_CLASS}
                    value={currentNode.minOccurs ?? 1}
                    onChange={(e) => updateNodeInDraft(currentPath, 'minOccurs', parseInt(e.target.value, 10) || 0)}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>maxOccurs</label>
                  <input
                    type="text"
                    className={INPUT_CLASS}
                    value={currentNode.maxOccurs || '1'}
                    onChange={(e) => updateNodeInDraft(currentPath, 'maxOccurs', e.target.value)}
                    placeholder="1 o unbounded"
                  />
                </div>
              </div>

              {/* Extensibilidad */}
              <div className="flex flex-wrap gap-4">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                    checked={Boolean(currentNode.abstractElement)}
                    onChange={(e) => updateNodeInDraft(currentPath, 'abstractElement', e.target.checked)}
                  />
                  abstract
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                    checked={Boolean(currentNode.nillable)}
                    onChange={(e) => updateNodeInDraft(currentPath, 'nillable', e.target.checked)}
                  />
                  nillable
                </label>
              </div>

              {/* substitutionGroup */}
              <div>
                <label className={LABEL_CLASS}>substitutionGroup</label>
                <input
                  type="text"
                  className={INPUT_CLASS}
                  value={currentNode.substitutionGroup || ''}
                  onChange={(e) => updateNodeInDraft(currentPath, 'substitutionGroup', e.target.value)}
                  placeholder="Nombre del elemento cabeza del grupo"
                />
              </div>
            </div>
          )}
        </div>

        {/* Wildcards (colapsable) */}
        <div className="mb-5 rounded-xl border border-indigo-200 bg-indigo-50/30">
          <button
            type="button"
            onClick={() => toggleSection(secWildcard)}
            className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-indigo-800"
          >
            <span className="flex items-center gap-2">
              <FaProjectDiagram className="text-indigo-600 text-xs" />
              Wildcards (xs:any / xs:anyAttribute)
            </span>
            {expandedSections[secWildcard] ? <FaChevronUp className="text-xs text-indigo-400" /> : <FaChevronDown className="text-xs text-indigo-400" />}
          </button>

          {expandedSections[secWildcard] && (
            <div className="border-t border-indigo-200 p-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                    checked={Boolean(currentNode.hasAny)}
                    onChange={(e) => updateNodeInDraft(currentPath, 'hasAny', e.target.checked)}
                  />
                  Incluir xs:any
                </label>
                {currentNode.hasAny && (
                  <div>
                    <label className={LABEL_CLASS}>processContents (xs:any)</label>
                    <Select
                      options={PROCESS_CONTENTS_OPTIONS}
                      value={PROCESS_CONTENTS_OPTIONS.find((o) => o.value === (currentNode.anyProcessContents || 'strict')) || null}
                      onChange={(opt) => updateNodeInDraft(currentPath, 'anyProcessContents', opt?.value || 'strict')}
                      menuPortalTarget={document.body}
                      styles={SELECT_STYLES}
                    />
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                    checked={Boolean(currentNode.hasAnyAttribute)}
                    onChange={(e) => updateNodeInDraft(currentPath, 'hasAnyAttribute', e.target.checked)}
                  />
                  Incluir xs:anyAttribute
                </label>
                {currentNode.hasAnyAttribute && (
                  <div>
                    <label className={LABEL_CLASS}>processContents (xs:anyAttribute)</label>
                    <Select
                      options={PROCESS_CONTENTS_OPTIONS}
                      value={PROCESS_CONTENTS_OPTIONS.find((o) => o.value === (currentNode.anyAttrProcessContents || 'strict')) || null}
                      onChange={(opt) => updateNodeInDraft(currentPath, 'anyAttrProcessContents', opt?.value || 'strict')}
                      menuPortalTarget={document.body}
                      styles={SELECT_STYLES}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Constraints */}
        <div className="mb-5">
          <ConstraintsSection
            constraints={currentNode.constraints || []}
            onChange={(newConstraints) => updateNodeInDraft(currentPath, 'constraints', newConstraints)}
          />
        </div>

        {/* Add field, add child, and collapse buttons. */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 w-full">
          <button
            type="button"
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-primary/30 bg-white px-4 py-2 text-xs sm:text-sm font-semibold text-primary shadow-xs transition hover:bg-primary/5 hover:shadow"
            onClick={() => setDraft((prev) => addFieldToNodeDraft(prev, currentPath))}
          >
            + Agregar campo
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-teal-500/40 bg-teal-50/70 px-4 py-2 text-xs sm:text-sm font-semibold text-teal-800 shadow-xs transition hover:bg-teal-100 hover:border-teal-500 hover:shadow"
            onClick={() => openInteropModalForNode(currentPath)}
            title="Importar campos filtrados desde conectores de dependencias gubernamentales"
          >
            <FaNetworkWired className="text-xs text-teal-600" />
            + Importar de Interoperabilidad
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs sm:text-sm font-semibold text-slate-600 shadow-xs transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary hover:shadow"
            onClick={() => {
              setDraft((prev) => addChildNodeDraft(prev, currentPath));
              setExpandedChildren((prev) => ({ ...prev, [childrenKey]: true }));
            }}
          >
            + Agregar hijo
          </button>
          {hasChildren && (
            <button
              type="button"
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-slate-100 px-4 py-2 text-xs sm:text-sm font-medium text-slate-700 transition hover:bg-slate-200"
              onClick={() => toggleChildren(currentPath)}
            >
              {showChildren ? <FaChevronUp className="text-[10px]" /> : <FaChevronDown className="text-[10px]" />}
              <span>{showChildren ? 'Colapsar hijos' : `Ver hijos (${rawChildren.length})`}</span>
            </button>
          )}
        </div>

        {/* Collapsed banner shown when hidden child nodes exist. */}
        {!showChildren && hasChildren && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-primary/30 bg-primary/5 px-4 py-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-primary">
              <FaSitemap className="text-xs" />
              <span>
                {rawChildren.length} subnodo{rawChildren.length > 1 ? 's' : ''} colapsado{rawChildren.length > 1 ? 's' : ''}:{' '}
                <span className="font-medium text-slate-700">
                  {rawChildren.map((c) => c.name || 'Sin nombre').join(', ')}
                </span>
              </span>
            </div>
            <button
              type="button"
              onClick={() => toggleChildren(currentPath)}
              className="flex items-center gap-1 text-xs font-bold text-primary hover:underline"
            >
              <FaChevronDown className="text-[10px]" />
              <span>Mostrar subnodos</span>
            </button>
          </div>
        )}

        {/* Fields. */}
        {hasFields && (
          <div className="mt-8 w-full">
            <div className="mb-5 border-b border-slate-100 pb-4">
              <h3 className="text-base font-bold text-slate-800 sm:text-lg">Campos del nodo</h3>
              <p className="mt-1 text-xs text-slate-500">Define los atributos o elementos hoja de este nodo.</p>
            </div>
            <div className="w-full space-y-6">
              {(currentNode.items || []).map((field, fieldIndex) =>
                renderField(field, fieldIndex, currentPath)
              )}
            </div>
          </div>
        )}

        {/* Child nodes. */}
        {showChildren && (
          <div className="mt-8 mx-auto w-full rounded-2xl border-l-4 border-l-primary border-y border-r border-slate-200 bg-slate-50/70 p-3 sm:p-5 lg:p-6 shadow-xs">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <FaSitemap className="text-xs" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-800">
                    Subnodos dependientes de "{currentNode.name || 'este elemento'}"
                  </h4>
                  <p className="text-xs text-slate-500">
                    {rawChildren.length} {rawChildren.length === 1 ? 'subnodo' : 'subnodos'} · Compositor: <span className="font-semibold text-primary">{currentNode.compositor || 'sequence'}</span>
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-2xs transition hover:bg-slate-50 hover:text-slate-900"
                onClick={() => toggleChildren(currentPath)}
              >
                <FaChevronUp className="text-[10px]" />
                <span>Colapsar subnodos</span>
              </button>
            </div>
            {hasChildren ? (
              <div className="w-full space-y-5">
                {rawChildren.map((childNode, childIndex) =>
                  renderDraftNode(childNode, [...currentPath, childIndex])
                )}
              </div>
            ) : (
              <div className="mx-auto max-w-xl rounded-xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center">
                <p className="text-sm font-medium text-slate-500">Sin subnodos</p>
                <p className="text-xs text-slate-400 mt-1">Puedes agregar subnodos para componer estructuras XML jerárquicas.</p>
              </div>
            )}
            <div className="mt-5 flex justify-center">
              <button
                type="button"
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-primary/30 bg-white px-4 py-2 text-xs sm:text-sm font-semibold text-primary shadow-xs transition hover:bg-primary/5 hover:shadow"
                onClick={() => {
                  setDraft((prev) => addChildNodeDraft(prev, currentPath));
                  setExpandedChildren((prev) => ({ ...prev, [childrenKey]: true }));
                }}
              >
                <FaPlus className="text-xs" />
                <span>Agregar subnodo a {currentNode.name || 'este elemento'}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

// RETURN PRINCIPAL

  return (
    <div className="flex flex-col h-full w-full bg-slate-50 text-slate-800 overflow-hidden font-sans">
      {extraForm}

      {/* Workspace top bar. */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-3 sm:px-6 py-3 sm:py-4 shadow-xs shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary shrink-0">
            <FaSitemap className="text-lg" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-primary">
                {entityName || 'Constructor XSD'}
              </span>
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-extrabold ${
                draft.status === 'PUBLISHED' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
              }`}>
                {draft.status === 'PUBLISHED' ? <FaCheck className="text-[9px]" /> : <FaPen className="text-[9px]" />}
                {draft.status === 'PUBLISHED' ? 'PUBLICADA' : 'BORRADOR'}
              </span>
              {draft.effectiveDate && (
                <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
                  <FaClock className="text-[9px]" /> Vigencia: {draft.effectiveDate}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-slate-500 font-medium">Versión:</span>
              <input
                type="text"
                disabled={draft.status === 'PUBLISHED'}
                value={draft.version || ''}
                onChange={(e) => updateDraft('version', e.target.value)}
                placeholder="1.0.0"
                className="w-24 rounded-lg border border-slate-300 bg-white px-2 py-0.5 text-sm font-bold text-slate-900 focus:border-primary focus:outline-none"
              />
              <label className="flex cursor-pointer items-center gap-1.5 ml-2 text-xs font-medium text-slate-600">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-slate-300 text-primary focus:ring-primary"
                  checked={Boolean(draft.current)}
                  onChange={(e) => updateDraft('current', e.target.checked)}
                />
                <span className="hidden sm:inline">Versión actual</span>
                <span className="sm:hidden">Actual</span>
              </label>
            </div>
          </div>
        </div>

        {/* Center: smart auto-save indicator. */}
        {isDraftMode && (
          <div className="flex items-center">
            {saveStatus === 'pending' && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 px-2.5 sm:px-3 py-1 text-[11px] sm:text-xs font-semibold text-blue-700 shadow-xs">
                <FaClock className="text-blue-500 text-xs animate-pulse" />
                <span className="hidden sm:inline">Cambios pendientes de auto-guardar</span>
                <span className="sm:hidden">Pendiente</span>
              </span>
            )}
            {saveStatus === 'saving' && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 sm:px-3 py-1 text-[11px] sm:text-xs font-semibold text-amber-700 shadow-xs">
                <FaSpinner className="animate-spin text-xs" />
                <span className="hidden sm:inline">Auto-guardando en BD...</span>
                <span className="sm:hidden">Guardando...</span>
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 sm:px-3 py-1 text-[11px] sm:text-xs font-semibold text-emerald-700 shadow-xs">
                <FaCheck className="text-xs" />
                <span className="hidden sm:inline">Guardado en BD {lastSavedTime ? `(${lastSavedTime})` : ''}</span>
                <span className="sm:hidden">Guardado</span>
              </span>
            )}
            {saveStatus === 'idle' && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 border border-slate-200 px-2.5 sm:px-3 py-1 text-[11px] sm:text-xs font-medium text-slate-600">
                <FaClock className="text-slate-400 text-xs" />
                <span className="hidden sm:inline">Auto-guardado activo</span>
                <span className="sm:hidden">Activo</span>
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 border border-red-200 px-2.5 sm:px-3 py-1 text-[11px] sm:text-xs font-semibold text-red-700 shadow-xs">
                <FaExclamationTriangle className="text-xs" />
                <span className="hidden sm:inline">Error al auto-guardar</span>
                <span className="sm:hidden">Error</span>
              </span>
            )}
          </div>
        )}

        {/* Acciones principales y utilidades */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowExamplesModal(true)}
            className="flex items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 transition hover:bg-amber-100 shadow-2xs"
            title="Cargar plantillas de esquemas preconfigurados"
          >
            <FaFlask className="text-amber-600" />
            <span className="hidden sm:inline">Plantillas</span>
          </button>

          <button
            type="button"
            onClick={() => setShowHelp(true)}
            className="flex items-center gap-1.5 rounded-xl border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-800 transition hover:bg-blue-100 shadow-2xs"
            title="Guía de reglas W3C XSD 1.0"
          >
            <FaQuestionCircle className="text-blue-600" />
            <span className="hidden sm:inline">Guía XSD</span>
          </button>

          {isDraftMode && onAutoSave && (
            <button
              type="button"
              className="flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-700 shadow-xs transition hover:bg-slate-100"
              onClick={handleSaveDraftManual}
              title="Guardar borrador inmediatamente en base de datos"
            >
              <FaSave className="text-slate-500" />
              <span>Guardar</span>
            </button>
          )}

          <button
            type="button"
            className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white shadow-xs transition hover:bg-emerald-700 hover:shadow-sm"
            onClick={handleRequestPublish}
            title="Validar contra reglas W3C y publicar esta versión"
          >
            <FaShieldAlt />
            <span>{saveLabel}</span>
          </button>

          {onClose && (
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              onClick={onClose}
              title="Cerrar constructor"
            >
              <FaTimes className="text-sm" />
            </button>
          )}
        </div>
      </div>

      {/* XSD module tabs. */}
      <div className="flex border-b border-slate-200 bg-slate-100/90 px-6 py-2 gap-2 overflow-x-auto shrink-0">
        <button
          type="button"
          onClick={() => setActiveModuleTab('structure')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap ${
            activeModuleTab === 'structure'
              ? 'bg-white text-primary shadow-xs border border-slate-200/80'
              : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
          }`}
        >
          <FaSitemap className="text-primary text-xs" />
          <span>1. Estructura y Árbol de Elementos</span>
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
            {metrics.nodeCount} nodos · {metrics.elemCount + metrics.attrCount} campos
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveModuleTab('namespace')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap ${
            activeModuleTab === 'namespace'
              ? 'bg-white text-primary shadow-xs border border-slate-200/80'
              : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
          }`}
        >
          <FaGlobe className="text-teal-600 text-xs" />
          <span>2. Namespace y Configuración XML</span>
          {draft.targetNamespace && (
            <span className="h-2 w-2 rounded-full bg-teal-500" title="Namespace configurado" />
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveModuleTab('effectiveDate')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap ${
            activeModuleTab === 'effectiveDate'
              ? 'bg-white text-primary shadow-xs border border-slate-200/80'
              : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
          }`}
        >
          <FaCalendarAlt className="text-indigo-600 text-xs" />
          <span>3. Fecha de Vigencia</span>
          {draft.effectiveDate && (
            <span className="rounded-full bg-indigo-100 text-indigo-800 px-1.5 py-0.2 text-[9px] font-bold">
              Programada
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveModuleTab('constraints')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap ${
            activeModuleTab === 'constraints'
              ? 'bg-white text-primary shadow-xs border border-slate-200/80'
              : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
          }`}
        >
          <FaKey className="text-amber-600 text-xs" />
          <span>4. Restricciones de Integridad</span>
          {metrics.constraintCount > 0 && (
            <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold">
              {metrics.constraintCount}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveModuleTab('xmlPreview')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition ml-auto whitespace-nowrap ${
            activeModuleTab === 'xmlPreview'
              ? 'bg-white text-emerald-700 shadow-xs border border-slate-200/80'
              : 'text-slate-600 hover:text-emerald-700 hover:bg-white/60'
          }`}
        >
          <FaCode className="text-emerald-600 text-xs" />
          <span>5. Vista Previa XML en Vivo</span>
          <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-bold">
            Live W3C
          </span>
        </button>
      </div>

      {/* Main module body with independent scrolling. */}
      <div className="flex-1 overflow-y-auto p-6 md:p-8">
        {/* Module 1: structure and tree. */}
        {activeModuleTab === 'structure' && (
          <div className="space-y-6 max-w-6xl mx-auto">
            {/* Source selector when a previous version exists. */}
            {previousVersion && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                  Origen de la estructura para la nueva versión
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleOriginChange('previous')}
                    className={`flex items-start gap-3 rounded-xl border p-4 text-left transition ${
                      originMode === 'previous'
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                        : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
                    }`}
                  >
                    <div className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border ${
                      originMode === 'previous' ? 'border-primary bg-primary text-white' : 'border-slate-400'
                    }`}>
                      {originMode === 'previous' && <span className="h-2 w-2 rounded-full bg-white" />}
                    </div>
                    <div>
                      <strong className="block text-sm font-semibold text-slate-800">
                        Partir de versión anterior ({previousVersion.version || 'Actual'})
                      </strong>
                      <span className="mt-0.5 block text-xs text-slate-500">
                        (Recomendado) Clona la jerarquía completa de nodos, campos y namespace previos para modificaciones incrementales.
                      </span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleOriginChange('blank')}
                    className={`flex items-start gap-3 rounded-xl border p-4 text-left transition ${
                      originMode === 'blank'
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                        : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'
                    }`}
                  >
                    <div className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border ${
                      originMode === 'blank' ? 'border-primary bg-primary text-white' : 'border-slate-400'
                    }`}>
                      {originMode === 'blank' && <span className="h-2 w-2 rounded-full bg-white" />}
                    </div>
                    <div>
                      <strong className="block text-sm font-semibold text-slate-800">
                        Comenzar desde cero (en blanco)
                      </strong>
                      <span className="mt-0.5 block text-xs text-slate-500">
                        Crea un esquema limpio con un elemento raíz nuevo sin importar la estructura anterior.
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Structure summary banner. */}
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-xs">
              <div>
                <h3 className="text-base font-bold text-slate-900">Jerarquía del Esquema XSD</h3>
                <p className="text-xs text-slate-500">
                  Agrega y organiza nodos complejos, elementos simples, atributos y restricciones.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs">
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-1 font-semibold text-emerald-700">
                  <FaSitemap className="text-emerald-500 text-xs" /> {metrics.nodeCount} Nodos
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 border border-blue-200 px-2.5 py-1 font-semibold text-blue-700">
                  <FaCube className="text-blue-500 text-xs" /> {metrics.elemCount} Elementos
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1 font-semibold text-amber-700">
                  <FaTag className="text-amber-500 text-xs" /> {metrics.attrCount} Atributos
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-purple-50 border border-purple-200 px-2.5 py-1 font-semibold text-purple-700">
                  <FaKey className="text-purple-500 text-xs" /> {metrics.constraintCount} Restricciones
                </span>
                <div className="flex items-center gap-1 border-l border-slate-200 pl-2">
                  <button
                    type="button"
                    onClick={() => setExpandedChildren({})}
                    className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100 transition"
                    title="Expandir todos los nodos y subnodos"
                  >
                    Expandir todo
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const allCollapsed = {};
                      const collapseAll = (nodes, path = []) => {
                        nodes.forEach((n, idx) => {
                          const p = [...path, idx];
                          allCollapsed[p.join('-')] = false;
                          const children = Array.isArray(n.childrens)
                            ? n.childrens
                            : (Array.isArray(n.children) ? n.children : []);
                          if (children.length) collapseAll(children, p);
                        });
                      };
                      collapseAll(draft.structure || []);
                      setExpandedChildren(allCollapsed);
                    }}
                    className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-100 transition"
                    title="Colapsar todos los subnodos"
                  >
                    Colapsar todo
                  </button>
                </div>
              </div>
            </div>

            {/* Node tree. */}
            <div className="flex w-full flex-col gap-6">
              {(draft.structure || []).map((node, nodeIndex) => renderDraftNode(node, [nodeIndex]))}
            </div>

            {/* Add root node button. */}
            <div className="pt-2 flex justify-center">
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                onClick={() => setDraft((prev) => ({
                  ...prev,
                  structure: [...cloneNodeTree(prev.structure || []), createEmptyNode()],
                }))}
              >
                <FaPlus className="text-xs" />
                Agregar elemento raíz nuevo
              </button>
            </div>
          </div>
        )}

        {/* Module 2: namespace and metadata. */}
        {activeModuleTab === 'namespace' && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="rounded-2xl border border-teal-200 bg-teal-50/50 p-5 shadow-xs">
              <div className="flex items-center gap-2.5 text-teal-800 font-bold text-base mb-2">
                <FaGlobe className="text-teal-600" />
                <span>Configuración de Namespace y Formularios XML</span>
              </div>
              <p className="text-xs text-teal-700 leading-relaxed">
                El <strong>targetNamespace</strong> es la URI que identifica globalmente los tipos y elementos de este esquema XSD.
                Los valores <strong>elementFormDefault</strong> y <strong>attributeFormDefault</strong> determinan si los elementos y atributos en documentos XML deben calificarse explícitamente con prefijo de namespace.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs space-y-5">
              <div>
                <label className={LABEL_CLASS}>targetNamespace (URI del Esquema)</label>
                <input
                  type="url"
                  className={INPUT_CLASS}
                  value={draft.targetNamespace || ''}
                  onChange={(e) => updateDraft('targetNamespace', e.target.value)}
                  placeholder="https://certificacion.queretaro.gob.mx/schema/titulacion"
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Ejemplo: <code>https://certificacion.queretaro.gob.mx/schema/v1</code>. Dejar vacío si no se requiere namespace calificado.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 pt-2 border-t border-slate-100">
                <div>
                  <label className={LABEL_CLASS}>elementFormDefault</label>
                  <Select
                    options={FORM_DEFAULT_OPTIONS}
                    value={FORM_DEFAULT_OPTIONS.find((o) => o.value === (draft.elementFormDefault || 'unqualified')) || null}
                    onChange={(opt) => updateDraft('elementFormDefault', opt?.value || 'unqualified')}
                    menuPortalTarget={document.body}
                    styles={SELECT_STYLES}
                  />
                  <p className="mt-1 text-[11px] text-slate-500">
                    <strong>unqualified:</strong> Los elementos locales no llevan prefijo. <br />
                    <strong>qualified:</strong> Todos los elementos deben llevar prefijo.
                  </p>
                </div>

                <div>
                  <label className={LABEL_CLASS}>attributeFormDefault</label>
                  <Select
                    options={FORM_DEFAULT_OPTIONS}
                    value={FORM_DEFAULT_OPTIONS.find((o) => o.value === (draft.attributeFormDefault || 'unqualified')) || null}
                    onChange={(opt) => updateDraft('attributeFormDefault', opt?.value || 'unqualified')}
                    menuPortalTarget={document.body}
                    styles={SELECT_STYLES}
                  />
                  <p className="mt-1 text-[11px] text-slate-500">
                    <strong>unqualified:</strong> Los atributos locales no llevan prefijo (estándar común en XSD).
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Module 3: effective date. */}
        {activeModuleTab === 'effectiveDate' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="rounded-2xl border border-indigo-200 bg-indigo-50/50 p-5 shadow-xs">
              <div className="flex items-center gap-2.5 text-indigo-900 font-bold text-base mb-2">
                <FaCalendarAlt className="text-indigo-600" />
                <span>Programación de Entrada en Vigor (Operacional)</span>
              </div>
              <p className="text-xs text-indigo-800 leading-relaxed">
                Permite fijar una fecha y hora exacta (ejemplo: <em>10 de octubre a las 13:45 hrs</em>) en la que esta versión del esquema XSD comenzará a regir.
                Hasta que se cumpla ese instante, el sistema mantendrá activa la versión anterior para todas las emisiones de certificados. Al llegar el momento, la nueva estructura entra en vigor automáticamente.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs space-y-4">
              <div>
                <label className={LABEL_CLASS}>Fecha y Hora de Inicio de Funcionamiento</label>
                <input
                  type="datetime-local"
                  className={INPUT_CLASS}
                  value={draft.effectiveDate || ''}
                  onChange={(e) => updateDraft('effectiveDate', e.target.value)}
                />
                <p className="mt-1.5 text-xs text-slate-500">
                  {draft.effectiveDate ? (
                    <span className="font-semibold text-indigo-700">
                      ⏰ Esta versión entrará en vigor el: {new Date(draft.effectiveDate).toLocaleString()}
                    </span>
                  ) : (
                    'Si se deja vacío, la versión entrará en vigor inmediatamente una vez sea publicada.'
                  )}
                </p>
              </div>

              {draft.effectiveDate && (
                <div className="flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                  <FaClock className="text-amber-600 shrink-0" />
                  <span>
                    La versión quedará marcada como <strong>Programada</strong> hasta la fecha seleccionada.
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Module 4: integrity constraints. */}
        {activeModuleTab === 'constraints' && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5 shadow-xs">
              <div className="flex items-center gap-2.5 text-amber-900 font-bold text-base mb-2">
                <FaKey className="text-amber-600" />
                <span>Restricciones de Integridad Referencial (W3C XSD)</span>
              </div>
              <p className="text-xs text-amber-800 leading-relaxed">
                Permiten garantizar la unicidad de campos y claves dentro del documento XML:
                <br />• <strong>unique:</strong> Garantiza valores únicos, permitiendo elementos nulos u opcionales.
                <br />• <strong>key:</strong> Exige unicidad estricta y obligatoria (sin nulos).
                <br />• <strong>keyref:</strong> Llave foránea referencial vinculada a una <em>key</em> existente.
              </p>
            </div>

            {metrics.constraintCount === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
                <FaKey className="mx-auto text-2xl text-slate-300 mb-2" />
                <h4 className="text-sm font-bold text-slate-700">Sin restricciones configuradas</h4>
                <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                  Puedes agregar restricciones de tipo <code>unique</code>, <code>key</code> o <code>keyref</code> directamente dentro de cada nodo en la pestaña de <strong>Estructura</strong>.
                </p>
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Restricciones Declaradas ({metrics.constraintCount})
                </h4>
                <div className="space-y-3">
                  {(draft.structure || []).map((node, nIdx) =>
                    (node.constraints || []).map((c, cIdx) => (
                      <div
                        key={`c-${nIdx}-${cIdx}`}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-xs"
                      >
                        <div className="flex items-center gap-2.5">
                          <span className={`rounded-md px-2 py-0.5 font-bold uppercase text-[10px] ${
                            c.kind === 'key' ? 'bg-purple-100 text-purple-800' : c.kind === 'keyref' ? 'bg-indigo-100 text-indigo-800' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {c.kind || 'unique'}
                          </span>
                          <strong className="text-slate-800 font-semibold">{c.name || 'Sin nombre'}</strong>
                          <span className="text-slate-400">en nodo:</span>
                          <span className="rounded-md bg-white border border-slate-200 px-1.5 py-0.5 text-slate-700 font-mono">
                            {node.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-slate-500">
                          <span>Selector: <code>{c.selector || '.'}</code></span>
                          <span>Campos: <code>{(c.fields || []).join(', ') || '—'}</code></span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Module 5: live XML preview. */}
        {activeModuleTab === 'xmlPreview' && (
          <div className="max-w-5xl mx-auto space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-xs">
              <div>
                <h3 className="text-base font-bold text-slate-900">Previsualización del Esquema XSD W3C</h3>
                <p className="text-xs text-slate-500">
                  Generado en tiempo real según la configuración activa de nodos, tipos, facets y namespace.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCopyXml}
                  className="flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 transition"
                >
                  {copiedXml ? <FaCheck className="text-emerald-600" /> : <FaCopy className="text-slate-500" />}
                  <span>{copiedXml ? 'Copiado' : 'Copiar XML'}</span>
                </button>
                <button
                  type="button"
                  onClick={handleDownloadXsd}
                  className="flex items-center gap-1.5 rounded-xl bg-primary px-3.5 py-1.5 text-xs font-bold text-white shadow-2xs hover:bg-primary/90 transition"
                >
                  <FaDownload />
                  <span>Descargar .xsd</span>
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 shadow-inner overflow-hidden">
              <pre className="font-mono text-xs leading-relaxed text-emerald-400 overflow-x-auto whitespace-pre max-h-[62vh]">
                {currentXmlPreview}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Module navigation footer. */}
      <div className="flex items-center justify-between border-t border-slate-200 bg-white px-6 py-3.5 shrink-0">
        <div className="text-xs text-slate-500 font-medium">
          Módulo: <strong className="text-slate-800 capitalize">{activeModuleTab}</strong> · {metrics.nodeCount} nodos · {metrics.elemCount + metrics.attrCount} campos
        </div>

        <div className="flex items-center gap-2">
          {onClose && (
            <button
              type="button"
              className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
              onClick={onClose}
            >
              Cerrar
            </button>
          )}

          {isDraftMode && onAutoSave && (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-2xs transition hover:bg-slate-50"
              onClick={handleSaveDraftManual}
            >
              <FaSave className="text-slate-500 text-xs" />
              Guardar borrador ahora
            </button>
          )}

          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2 text-xs font-bold text-white shadow-xs transition hover:bg-emerald-700 hover:shadow-md"
            onClick={handleRequestPublish}
          >
            <FaShieldAlt className="text-xs" />
            {saveLabel}
          </button>
        </div>
      </div>

      {/* Publication confirmation modal. */}
      {showPublishModal && (
        <PublishConfirmModal
          draft={draft}
          onConfirm={handleConfirmPublish}
          onCancel={() => setShowPublishModal(false)}
        />
      )}

      {/* Help panel. */}
      {showHelp && <XsdHelpPanel onClose={() => setShowHelp(false)} />}

      {/* Test examples modal. */}
      {showExamplesModal && (
        <XsdExamplesModal
          onSelect={(example) => {
            setDraft((prev) => ({
              ...prev,
              targetNamespace: example.targetNamespace || '',
              elementFormDefault: example.elementFormDefault || 'qualified',
              attributeFormDefault: example.attributeFormDefault || 'unqualified',
              structure: cloneNodeTree(example.structure || []),
            }));
            setExpandedChildren({});
            setShowExamplesModal(false);
            toast.success(`Ejemplo "${example.name}" cargado exitosamente`);
          }}
          onClose={() => setShowExamplesModal(false)}
        />
      )}

      {/* Interoperability import modal. */}
      {showInteropModal && (
        <InteropImportModal
          catalog={interopCatalog}
          loading={loadingInteropCatalog}
          onImport={handleImportInteropFields}
          onClose={() => setShowInteropModal(false)}
        />
      )}
    </div>
  );
}

export default NewXsdVersion;
